import React, { useEffect, useState } from "react";
import { ArrowUpRight, BookOpen, Calendar } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=12`)
      .then(res => setPosts(res.data || []))
      .catch(() => setError("Could not load blog posts right now."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "90px", paddingBottom: "80px", position: "relative" }}>
      <SEO
        title="Astrology Insights & Vedic Wisdom — AstroFate Blog"
        description="Read the latest articles from AstroFate astrologers — planetary updates, Vedic remedies, dasha analyses, and life guidance grounded in ancient astrology and modern AI."
      />
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(91,30,140,0.18), transparent)", top: "-100px", right: "-150px", borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(45,11,69,0.3), transparent)", bottom: "-80px", left: "-120px", borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 16px", position: "relative", zIndex: 1 }}>
        <div className="animate-fadeInUp" style={{ textAlign: "center", marginBottom: "44px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(255,209,102,0.08)", border: "1px solid rgba(255,209,102,0.2)", borderRadius: "50px", padding: "6px 16px", marginBottom: "18px" }}>
            <BookOpen size={14} style={{ color: "#FFD166" }} />
            <span style={{ color: "#FFD166", fontSize: "0.78rem", fontWeight: "600" }}>Astrology Insights</span>
          </div>
          <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", marginBottom: "10px" }}>The AstroFate Blog</h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "1rem", maxWidth: "620px", margin: "0 auto" }}>
            Vedic wisdom, planetary updates and life guidance — straight from our astrologers.
          </p>
        </div>

        {loading && (
          <div data-testid="blog-loading" style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <div className="cosmic-spinner" />
          </div>
        )}

        {error && !loading && (
          <div style={{ background: "rgba(239,68,68,0.1)", color: "#fca5a5", padding: "14px 18px", borderRadius: "12px", textAlign: "center" }}>{error}</div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div style={{ color: "rgba(255,255,255,0.5)", textAlign: "center", padding: "40px 0" }}>
            No posts yet. Please check back soon.
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div data-testid="blog-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "22px" }}>
            {posts.map((p, i) => (
              <a
                key={p.link || p.guid || p.title || i}
                href={p.link}
                target="_blank"
                rel="noreferrer"
                data-testid={`blog-card-${i}`}
                className="glass-card glass-card-hover"
                style={{
                  display: "flex", flexDirection: "column", textDecoration: "none",
                  border: "1px solid rgba(255,209,102,0.12)", overflow: "hidden", padding: 0,
                  transition: "transform 200ms ease, border-color 200ms ease",
                }}
              >
                {p.image && (
                  <div style={{
                    width: "100%", aspectRatio: "16/9", background: `url(${p.image}) center/cover, rgba(91,30,140,0.2)`,
                    borderBottom: "1px solid rgba(255,209,102,0.08)",
                  }} />
                )}
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginBottom: "8px" }}>
                    <Calendar size={11} /> {formatDate(p.published)} · {p.author}
                  </div>
                  <h2 className="font-cinzel" style={{ color: "white", fontSize: "1.02rem", lineHeight: 1.35, marginBottom: "10px", minHeight: "2.7em" }}>
                    {p.title}
                  </h2>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", lineHeight: 1.55, marginBottom: "14px", flex: 1 }}>{p.excerpt}</p>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#FFD166", fontSize: "0.82rem", fontWeight: 600 }}>
                    Read on TheAstroFate <ArrowUpRight size={14} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "48px" }}>
          <a
            href="https://www.theastrofate.com/blog"
            target="_blank"
            rel="noreferrer"
            data-testid="blog-view-all"
            className="btn-ghost"
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none", fontSize: "0.88rem", padding: "10px 22px" }}
          >
            View all posts on TheAstroFate <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}
